import { createTheme } from '@mui/material/styles';

// Smart Feed Dark Theme
export const smartFeedTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#667eea',
      light: '#7c92ff',
      dark: '#4c5fd5',
    },
    secondary: {
      main: '#764ba2',
      light: '#9d6bc8',
      dark: '#5a3880',
    },
    background: {
      default: '#0b0e1a',
      paper: '#1a1d2e',
    },
    text: {
      primary: '#E0E0E0',
      secondary: 'rgba(224, 224, 224, 0.7)',
    },
    divider: 'rgba(255, 255, 255, 0.1)',
    // Domain colors
    finance: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    social: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
    },
    health: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    professional: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    personal: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#7c3aed',
    },
    time: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    habitation: {
      main: '#06b6d4',
      light: '#22d3ee',
      dark: '#0891b2',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.2)',
    '0 2px 6px rgba(0,0,0,0.2)',
    '0 4px 12px rgba(0,0,0,0.25)',
    '0 6px 16px rgba(0,0,0,0.3)',
    '0 8px 20px rgba(0,0,0,0.35)',
    '0 12px 24px rgba(0,0,0,0.4)',
    '0 16px 32px rgba(0,0,0,0.45)',
    '0 20px 40px rgba(0,0,0,0.5)',
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
        },
        contained: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
  },
});

// Utility function to get domain color
export const getDomainColor = (domain, theme) => {
  const colorMap = {
    Finance: theme.palette.finance.main,
    Social: theme.palette.social.main,
    Health: theme.palette.health.main,
    Professional: theme.palette.professional.main,
    Personal: theme.palette.personal.main,
    Time: theme.palette.time.main,
    Habitation: theme.palette.habitation.main,
    General: theme.palette.primary.main,
  };
  return colorMap[domain] || theme.palette.primary.main;
};

// Utility function to get type gradient
export const getTypeGradient = (type) => {
  const gradients = {
    Actionable: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    Summary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    Reminder: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    Publication: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    Motivational: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    Custom: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    News: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  };
  return gradients[type] || gradients.Custom;
};

export default smartFeedTheme;
